// usePayrollPeriod.js — 급여 월별: 볼 달(기본 달 자동) + 그 달 급여 기간 불러오기
//   PayrollMonthly.jsx(352줄)에서 그대로 옮김(2026-10-28, 300줄 규칙, 동작 변화 없음).
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import { nowKST } from '../../../utils/date';

export function usePayrollPeriod(companyCode) {
  // nowKST 사용 (new Date 직접 사용 금지 — utils/date.js 사용)
  const now = nowKST();
  const [year, setYear]   = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [defaultMonthReady, setDefaultMonthReady] = useState(false);
  const userPickedMonthRef = useRef(false);
  // 현재 선택된 기간의 데이터
  const [periodData, setPeriodData] = useState(null); // { period, records }
  const [loading, setLoading] = useState(false);

  function handleMonthChange(next) {
    userPickedMonthRef.current = true;
    setYear(Number(next.year));
    setMonth(Number(next.month));
  }

  async function handleDefaultMonth() {
    if (!companyCode) return;
    const res = await apiClient.get(`/api/admin/payroll/default-month?company_code=${companyCode}`);
    if (!res.ok || !res.data?.data) return;
    userPickedMonthRef.current = false;
    setYear(Number(res.data.data.year));
    setMonth(Number(res.data.data.month));
  }

  useEffect(() => {
    let alive = true;
    userPickedMonthRef.current = false;
    setDefaultMonthReady(false);

    const applyDefaultMonth = async () => {
      const res = await apiClient.get(`/api/admin/payroll/default-month?company_code=${companyCode}`);
      if (alive && res.ok && res.data?.data && !userPickedMonthRef.current) {
        setYear(Number(res.data.data.year));
        setMonth(Number(res.data.data.month));
      }
      if (alive) setDefaultMonthReady(true);
    };

    if (companyCode) applyDefaultMonth();
    else setDefaultMonthReady(true);
    return () => { alive = false; };
  }, [companyCode]);

  // ── 기간 데이터 로드 ──────────────────────────────────────────────────────
  const fetchPeriod = useCallback(async () => {
    if (!companyCode || !defaultMonthReady) return;
    setLoading(true);
    // 기간 목록에서 해당 연/월 찾기
    const listRes = await apiClient.get(`/api/admin/payroll/monthly/periods?company_code=${companyCode}`);
    if (listRes.ok && listRes.data?.data) {
      const found = listRes.data.data.find((p) => p.year === year && p.month === month);
      if (found) {
        // 상세 기록 로드
        const detailRes = await apiClient.get(`/api/admin/payroll/monthly/periods/${found.id}`);
        if (detailRes.ok && detailRes.data?.data) {
          setPeriodData(detailRes.data.data);
        } else { setPeriodData(null); }
      } else { setPeriodData(null); }
    } else { setPeriodData(null); }
    setLoading(false);
  }, [companyCode, defaultMonthReady, year, month]);

  useEffect(() => { fetchPeriod(); }, [fetchPeriod]);

  return { year, month, defaultMonthReady, periodData, loading, fetchPeriod, handleMonthChange, handleDefaultMonth };
}
